import React, { useState, useEffect } from 'react';
import { Star, Quote, Plus, X, Send, Check } from 'lucide-react';
import { useCMS } from '../store/cmsStore';

interface UserTestimonial {
  name: string;
  role: string;
  text: string;
  rating: number;
  avatar: string;
  isUser?: boolean;
}

export default function Testimonials() {
  const { data } = useCMS();
  const [userTestimonials, setUserTestimonials] = useState<UserTestimonial[]>([]);
  const [showForm, setShowForm] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    city: '',
    rating: 5,
    text: '',
  });

  useEffect(() => {
    const saved = localStorage.getItem('sudra_testimonials');
    if (saved) {
      try {
        setUserTestimonials(JSON.parse(saved));
      } catch (e) {}
    }
  }, []);

  // Get testimonials from CMS
  const cmsTestimonials = data.testimonials
    .filter(t => t.active)
    .map(t => ({
      name: t.name,
      role: 'من الرياض',
      text: t.text,
      rating: t.rating,
      avatar: t.name.charAt(0),
      date: t.date,
    }));

  const allTestimonials = [...userTestimonials, ...cmsTestimonials];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.text.trim()) return;

    const newTestimonial: UserTestimonial = {
      name: formData.name,
      role: formData.city ? `من ${formData.city}` : 'من الرياض',
      text: formData.text,
      rating: formData.rating,
      avatar: formData.name.charAt(0),
      isUser: true,
    };

    const updated = [newTestimonial, ...userTestimonials];
    setUserTestimonials(updated);
    localStorage.setItem('sudra_testimonials', JSON.stringify(updated));

    setFormData({ name: '', city: '', rating: 5, text: '' });
    setShowForm(false);
    setShowSuccess(true);
    setTimeout(() => setShowSuccess(false), 3000);
  };

  return (
    <section id="testimonials" className="py-12 md:py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 md:mb-16">
          <span className="inline-block bg-gold-100 text-gold-700 px-4 py-2 rounded-full text-sm font-cairo font-medium mb-4">
            آراء عملائنا
          </span>
          <h2 className="text-2xl md:text-3xl lg:text-4xl font-amiri font-bold text-gray-900 mb-4">
            ماذا يقول عملاؤنا؟
          </h2>
          <p className="text-gray-600 font-cairo max-w-2xl mx-auto text-sm md:text-base">
            نفخر بثقة عملائنا ونسعد بمشاركتهم تجاربهم معنا
          </p>
        </div>

        {/* Add Testimonial Button */}
        <div className="text-center mb-8">
          <button
            onClick={() => setShowForm(!showForm)}
            className="inline-flex items-center gap-2 bg-brown-600 text-white px-6 py-3 rounded-xl font-cairo font-bold hover:bg-brown-700 transition"
          >
            <Plus className="w-5 h-5" />
            أضف تقييمك
          </button>
        </div>

        {/* Success Message */}
        {showSuccess && (
          <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-8 text-center">
            <div className="flex items-center justify-center gap-2 text-green-700 font-cairo">
              <Check className="w-5 h-5" />
              تم إضافة تقييمك بنجاح! شكراً لك
            </div>
          </div>
        )}

        {/* Add Testimonial Form */}
        {showForm && (
          <div className="bg-gray-50 rounded-2xl p-6 md:p-8 mb-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-lg font-bold text-gray-900 font-cairo">أضف تقييمك</h3>
              <button onClick={() => setShowForm(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">الاسم</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-brown-500 focus:border-transparent font-cairo"
                    placeholder="اسمك الكريم"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">المدينة</label>
                  <input
                    type="text"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-brown-500 focus:border-transparent font-cairo"
                    placeholder="مدينتك (اختياري)"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">التقييم</label>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <button
                      key={star}
                      type="button"
                      onClick={() => setFormData({ ...formData, rating: star })}
                      className="focus:outline-none"
                    >
                      <Star
                        className={`w-8 h-8 transition ${
                          star <= formData.rating
                            ? 'text-yellow-400 fill-current'
                            : 'text-gray-300 hover:text-yellow-200'
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 font-cairo mb-2">تقييمك</label>
                <textarea
                  value={formData.text}
                  onChange={(e) => setFormData({ ...formData, text: e.target.value })}
                  className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-brown-500 focus:border-transparent font-cairo"
                  rows={4}
                  placeholder="اكتب تجربتك معنا..."
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full bg-brown-600 text-white py-3 rounded-xl font-cairo font-bold hover:bg-brown-700 transition flex items-center justify-center gap-2"
              >
                <Send className="w-5 h-5" />
                إرسال التقييم
              </button>
            </form>
          </div>
        )}

        {/* Testimonials Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {allTestimonials.slice(0, 9).map((testimonial, index) => (
            <div
              key={index}
              className={`bg-white rounded-2xl p-6 shadow-sm border transition-all hover:shadow-lg ${
                testimonial.isUser ? 'border-brown-200 bg-brown-50' : 'border-gray-100'
              }`}
            >
              {/* Quote Icon */}
              <Quote className="w-8 h-8 text-brown-200 mb-4" />

              {/* Rating */}
              <div className="flex gap-1 mb-4">
                {Array.from({ length: testimonial.rating }).map((_, i) => (
                  <Star key={i} className="w-4 h-4 text-yellow-400 fill-current" />
                ))}
              </div>

              {/* Text */}
              <p className="text-gray-700 font-cairo text-sm md:text-base leading-relaxed mb-6">
                "{testimonial.text}"
              </p>

              {/* Author */}
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-brown-100 flex items-center justify-center">
                  <span className="text-brown-700 font-cairo font-bold">{testimonial.avatar}</span>
                </div>
                <div>
                  <p className="font-cairo font-bold text-gray-900 text-sm">{testimonial.name}</p>
                  <p className="font-cairo text-gray-500 text-xs">{testimonial.role}</p>
                </div>
                {testimonial.isUser && (
                  <span className="mr-auto bg-brown-100 text-brown-700 px-2 py-1 rounded text-xs font-cairo">
                    عميل
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>

        {/* View More */}
        {allTestimonials.length > 9 && (
          <div className="text-center mt-8">
            <p className="text-gray-500 font-cairo">
              يعرض 9 من أصل {allTestimonials.length} تقييم
            </p>
          </div>
        )}
      </div>
    </section>
  );
}